import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "tiger_cart";

const itemKey = (i) => `${i.id}|${i.size}|${i.color || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, size, color = null) => {
    const newKey = itemKey({ id: product.id, size, color: color?.name });
    setItems((prev) => {
      const existing = prev.find((i) => itemKey(i) === newKey);
      if (existing) {
        return prev.map((i) => (itemKey(i) === newKey ? { ...i, qty: i.qty + 1 } : i));
      }
      return [
        ...prev,
        {
          id: product.id,
          title: product.title,
          price: product.price,
          image: color?.image || product.image,
          size,
          color: color?.name || null,
          qty: 1,
        },
      ];
    });
    toast.success(`${product.title} (${size}${color ? " · " + color.name : ""}) added to cart`);
  };

  const remove = (id, size, color) =>
    setItems((prev) => prev.filter((i) => itemKey(i) !== itemKey({ id, size, color })));

  const changeQty = (id, size, color, delta) =>
    setItems((prev) =>
      prev
        .map((i) => (itemKey(i) === itemKey({ id, size, color }) ? { ...i, qty: i.qty + delta } : i))
        .filter((i) => i.qty > 0)
    );

  const clear = () => setItems([]);

  const value = useMemo(
    () => ({
      items,
      open,
      setOpen,
      add,
      remove,
      changeQty,
      clear,
      count: items.reduce((sum, i) => sum + i.qty, 0),
      subtotal: items.reduce((sum, i) => sum + i.price * i.qty, 0),
    }),
    [items, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
